//#region Util Imports

import * as _ from 'underscore'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

//#region IMR Imports

import { IMRPopupOptions, IIMRPopup } from '@imr/controls.angularjs/components/overlay/popup'
import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base'

//#endregion

import { Enums } from '../../../../../../models/common'
import { OpPackage } from '../../../../../../models/core/op-package'

import { TypeHelper, LocalizationHelper, Dictionary } from "../../../../../../helpers"

import { ColumnModel } from '../../../../../api/api.models'

import { GridTypes, GridController, GridOptions, GridItemAppearanceOptions } from '../../../../../common/grid'

import { OrderConfirmService } from '../../../../../../services/order/order.confirm.service'
import { OrderService } from '../../../../../../services/order'

import { ConfirmOrderCreateOrderOptions, IConfirmOrderCreateOrder } from './confirm-order-create-order.interfaces'

export class ConfirmOrderCreateOrderController implements ng.IComponentController, IConfirmOrderCreateOrder {

    // #region Consts

    private get CONFRIM_ORDER_CREATE_ORDER_GRID_NAME(): string { return 'confrimOrderCreateGrid' }

    // #endregion

    // #region Memebers

    public imrPopupOptions: IMRPopupOptions

    private columnNameDict: Dictionary<string>
    private checkBoxOptions: DevExpress.ui.dxCheckBoxOptions
    private continueButton: DevExpress.ui.dxButton
    private canClose: boolean = true
    private dataGrid: DevExpress.ui.dxDataGrid
    private gridController: GridController<any>
    private gridOptions: GridOptions
    private isLoading: boolean = false
    private newDataSource: OpPackage[] = []
    private options: ConfirmOrderCreateOrderOptions
    private currentDataSource: OpPackage[]
    private imrPopup: IIMRPopup
    private toolbarOptions: DevExpress.ui.dxToolbarOptions

    // #endregion


    // #region ng.IComponentController Implementation

    public $onChanges(changes: { [index: string]: ng.IChangesObject<any> }): void {
        if (TypeHelper.isOptionsDefined(changes)) {
            this.initialize()
        }
    }

    // #endregion


    // #region Ctor

    constructor(
        private orderService: OrderService,
        private confirmOrderService: OrderConfirmService
    ) { }

    // #endregion

    // #region Component

    private initialize(): void {
        this.getColumnNamesForDataParser()
        this.initializeImrPopupOptions()
        this.initializeGridOptions()
        this.onInitialized()
    }

    // #endregion


    // #region ImrPopup

    private initializeImrPopupOptions(): void {
        this.imrPopupOptions = {
            bottomToolbarOptions: {
                innerOptions: {
                    items: [{
                        location: 'after',
                        widget: 'dxButton',
                        options: {
                            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save),
                            onClick: (e: any) => {
                                this.onSaveButtonClick()
                            },
                            onInitialized: (e: any) => {
                                this.continueButton = e.component
                            }
                        }
                    }]
                }
            },

            innerOptions: {
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SaveOrders),
                deferRendering: true,
                onHiding: () => this.gridController.clearItemDisplayStates()
            },

            onInitialized: (e: IMRComponentInitializedArgs<IIMRPopup>) => {
                this.imrPopup = e.component
                this.imrPopup.show()
            }
        }
    }

    private onSaveButtonClick(): void {
        if (this.canClose) {
            this.canClose = false
            this.onSaveOrder()
        }
        else {
            this.hide()
        }
        this.toggleContinueButton()
    }

    private toggleContinueButton(): void {
        if (this.canClose) {
            this.continueButton.option('text', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Save))
        } else {
            this.continueButton.option('text', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Close))
        }
    }

    private handleContinueButton(disabled: boolean): void {
        this.continueButton.option('disabled', !disabled)
        if (disabled) {
            this.continueButton.option('text', LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Close))
        }
    }

    // #endregion


    // #region Grid

    private initializeGridOptions(): any {
        this.currentDataSource = this.options.gridDataSource
        this.orderService.getMainGridColumns()
            .then((defaultColumns: ColumnModel[], columns: ColumnModel[]) => {

                const errorInfoColumn: ColumnModel = this.confirmOrderService.getErrorInfoColumn()

                defaultColumns.push(errorInfoColumn)
                columns.push(errorInfoColumn)

                this.gridOptions = {
                    type: GridTypes.PAGINATED_SMALL,
                    name: this.CONFRIM_ORDER_CREATE_ORDER_GRID_NAME,
                    buttonsOptions: {
                        enabled: true
                    },
                    getDataSource: () => this.currentDataSource,
                    dataSourceParser: this.dataSourceParser(),
                    columnsDictionary: columns,
                    defaultColumnsDictionary: defaultColumns,
                    onInitialized: (e: any, grid: GridController<any>) => {
                        this.gridController = grid
                    },
                    masterDetail: {
                        enabled: true,
                        template: 'errors',
                        autoExpandAll: true,
                    }
                }
            })
    }

    private dataSourceParser(): (obj: any) => { sourceObject?: any; additionalFields?: { key: string; value: any; }[]; } {
        return (pack: OpPackage) => {
            return {
                additionalFields: [{
                    key: this.columnNameDict.tryGetValue(Enums.DataType.HELPER_NAME),
                    value: pack.name
                },
                {
                    key: this.columnNameDict.tryGetValue(Enums.DataType.HELPER_ID_PACKAGE),
                    value: pack.idPackage
                },
                {
                    key: this.confirmOrderService.ERROR_INFO_COLUMN_NAME,
                    value: pack[this.confirmOrderService.ERROR_INFO_COLUMN_NAME] != null ? pack[this.confirmOrderService.ERROR_INFO_COLUMN_NAME] : ''
                }]
            }
        }
    }

    private getColumnNamesForDataParser(): void {
        this.confirmOrderService
            .getColumnNamesForDataParser()
            .then((dictionary: Dictionary<string>) => {
                this.columnNameDict = dictionary
            })
    }

    // #endregion


    // #region Events

    private onInitialized(): void {
        if (this.options != null && _.isFunction(this.options.onInitialized)) {
            this.options.onInitialized(this)
        }
    }

    private onSaveOrder(): void {
        if (this.options != null && _.isFunction(this.options.onSaveOrder)) {
            this.options.onSaveOrder(this.currentDataSource)
        }
    }


    // #endregion

    // #region IConfirmOrderCreateOrder

    public refreshDataGrid(): void {
        this.gridController.setDataSource(this.newDataSource)
        this.newDataSource = []
        this.handleContinueButton(true)
    }

    public updateSavedRow(item: any, options: GridItemAppearanceOptions): void {
        this.gridController.setItemDisplayState(item, options)
        this.newDataSource.push(item)
        this.gridController.dxGrid.refresh()
    }

    public show(): void {
        this.imrPopup.show()
        if (this.gridController != null) {
            this.gridController.clearItemDisplayStates()
        }
    }

    public hide(): void {
        this.imrPopup.hide()
        this.isLoading = false
    }

    public updateConfirmGridDataSource(source: OpPackage[]): void {
        if (source != null && this.gridController != null) {
            this.canClose = true
            this.toggleContinueButton()
            this.currentDataSource = source
            this.gridController.setDataSource(this.currentDataSource)
        }
        this.show()
    }

    // #endregion
}